export interface HSL {
  h: number;
  s: number;
  l: number;
}

export function hexToHSL(hex: string): HSL {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '');

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  
  // Length alone is not a hex check: parseInt() returns NaN for non-hex digits
  // ('zzzzzz' is six characters long), which would emit `--primary: NaN NaN% NaN%`.
  if (!/^[0-9a-f]{6}$/i.test(hex)) return { h: 0, s: 0, l: 0 }; // Fallback

  // Parse r, g, b values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

export const isHexColor = (value: unknown): value is string => typeof value === 'string' && /^#?(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);
export function normalizeHex(value: unknown, fallback = '#6d28d9'): string {
  if (!isHexColor(value)) return fallback;
  const hex = value.replace(/^#/, '');
  return '#' + (hex.length === 3 ? [...hex].map(char => char + char).join('') : hex).toLowerCase();
}

const formatHsl = ({ h, s, l }: HSL) => `${h} ${s}% ${l}%`;
export function hslLuminance({ h, s, l }: HSL): number {
  const saturation = s / 100, lightness = l / 100;
  const a = saturation * Math.min(lightness, 1 - lightness);
  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    const v = lightness - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(8) + 0.0722 * channel(4);
}
export function contrastRatio(a: HSL, b: HSL): number {
  const x = hslLuminance(a), y = hslLuminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
export function readableColor(preferred: HSL, background: HSL, minimum = 4.5, otherSurfaces: HSL[] = []): HSL {
  const readable = (color: HSL) => [background, ...otherSurfaces].every(surface => contrastRatio(color, surface) >= minimum);
  if (readable(preferred)) return preferred;
  // Preserve hue/saturation and choose the closest passing lightness.
  const options = Array.from({ length: 101 }, (_, l) => ({ ...preferred, l }))
    .filter(readable)
    .sort((a, b) => Math.abs(a.l - preferred.l) - Math.abs(b.l - preferred.l));
  return options[0] ?? { h: 0, s: 0, l: hslLuminance(background) > 0.179 ? 0 : 100 };
}

export function generateThemeVariables(
  baseColorHex: string, radius: number, isDark = false,
  secondaryColorHex?: string, backgroundColorHex?: string, textColorHex?: string,
  borderColorHex?: string, borderWidth?: number,
) {
  const primary = hexToHSL(normalizeHex(baseColorHex));
  const background = isHexColor(backgroundColorHex) ? hexToHSL(backgroundColorHex) : { h: primary.h, s: isDark ? 30 : 20, l: isDark ? 8 : 98 };
  const darkSurface = background.l < 50;
  const surface = { ...background, s: Math.min(background.s, 30), l: darkSurface ? Math.min(background.l + 4, 50) : Math.min(background.l + 2, 100) };
  const muted = { ...background, s: Math.min(background.s, 20), l: darkSurface ? Math.min(background.l + 10, 50) : Math.max(background.l - 6, 50) };
  const secondary = isHexColor(secondaryColorHex) ? hexToHSL(secondaryColorHex) : { h: primary.h, s: Math.max(10, primary.s - 20), l: darkSurface ? 20 : 90 };
  const text = isHexColor(textColorHex) ? hexToHSL(textColorHex) : { h: background.h, s: 20, l: darkSurface ? 90 : 10 };
  const border = isHexColor(borderColorHex) ? hexToHSL(borderColorHex) : { ...background, s: 20, l: darkSurface ? Math.min(background.l + 22, 60) : Math.max(background.l - 20, 30) };
  const on = (color: HSL, preferred = text) => formatHsl(readableColor(preferred, color));
  const primaryText = contrastRatio({ h: 0, s: 0, l: 0 }, primary) >= contrastRatio({ h: 0, s: 0, l: 100 }, primary) ? '0 0% 0%' : '0 0% 100%';
  const destructive = { h: 0, s: 72, l: darkSurface ? 40 : 45 };
  return {
    '--background': formatHsl(background), '--foreground': on(background),
    '--card': formatHsl(surface), '--card-foreground': on(surface),
    '--popover': formatHsl(surface), '--popover-foreground': on(surface),
    '--primary': formatHsl(primary), '--primary-foreground': primaryText,
    '--primary-text': formatHsl(readableColor(primary, background, 4.5, [surface])),
    '--secondary': formatHsl(secondary), '--secondary-foreground': on(secondary),
    '--btn-surface': formatHsl(background), '--btn-surface-text': on(background), '--btn-surface-border': formatHsl(border),
    '--muted': formatHsl(muted), '--muted-foreground': formatHsl(readableColor({ ...text, l: darkSurface ? 65 : 40 }, background, 4.5, [surface, muted])),
    '--accent': formatHsl(secondary), '--accent-foreground': on(secondary),
    '--destructive': formatHsl(destructive), '--destructive-foreground': on(destructive, { h: 0, s: 0, l: 100 }),
    '--border': formatHsl(border), '--input': formatHsl(readableColor(border, background, 3, [surface])),
    '--ring': formatHsl(readableColor(primary, background, 3, [surface])),
    '--radius': `${Number.isFinite(radius) ? Math.max(0, Math.min(radius, 2)) : 0.5}rem`,
    ...(Number.isFinite(borderWidth) ? { '--custom-border-width': `${Math.max(0, Math.min(borderWidth!, 4))}px` } : {}),
  };
}
